import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Save, Download, Network, List } from 'lucide-react'
import type { RelationshipResult, CharacterNode, RelationshipEdge } from '@creanizer/types'
import { RELATIONSHIP_TYPE_LABELS, RELATIONSHIP_TYPE_COLORS } from '@creanizer/types'
import { D3RelationshipGraph } from './D3RelationshipGraph'

interface RelationshipGraphProps {
  result: RelationshipResult
  onSave: () => void
  isSaved?: boolean
}

export function RelationshipGraph({ result, onSave, isSaved }: RelationshipGraphProps) {
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterNode | null>(null)
  const [viewMode, setViewMode] = useState<'graph' | 'list'>('graph') // デフォルトを相関図に変更

  // result.charactersとresult.relationshipsが存在することを確認
  if (!result || !result.characters || !result.relationships) {
    return (
      <div className="card">
        <div className="p-6">
          <p className="text-muted-foreground">生成結果を取得中...</p>
        </div>
      </div>
    )
  }

  const getCharacterRelationships = (characterId: string) => {
    return result.relationships.filter(
      rel => rel.source === characterId || rel.target === characterId
    )
  }

  const handleExport = () => {
    const exportData = {
      summary: result.summary,
      characters: result.characters,
      relationships: result.relationships.map(rel => ({
        from: result.characters.find(c => c.id === rel.source)?.name,
        to: result.characters.find(c => c.id === rel.target)?.name,
        type: RELATIONSHIP_TYPE_LABELS[rel.relationshipType as keyof typeof RELATIONSHIP_TYPE_LABELS] || rel.relationshipType,
        fromToRelation: rel.fromSourceToTarget,
        toFromRelation: rel.fromTargetToSource,
        strength: rel.strength,
        description: rel.description
      }))
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `relationship_${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="card">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">生成結果</h3>
          <div className="flex gap-2">
            <Button 
              variant={isSaved ? 'secondary' : 'outline'} 
              size="sm" 
              onClick={onSave}
              disabled={isSaved}
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaved ? '保存済み' : '保存'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              エクスポート
            </Button>
          </div>
        </div>
      </div>
      <div className="px-6 pb-6 space-y-4">
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-sm">{result.summary}</p>
        </div>

        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as any)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="graph">
              <Network className="h-4 w-4 mr-2" />
              相関図
            </TabsTrigger>
            <TabsTrigger value="list">
              <List className="h-4 w-4 mr-2" />
              リスト表示
            </TabsTrigger>
          </TabsList>

          <TabsContent value="graph" className="min-h-[700px]">
            <D3RelationshipGraph result={result} />
          </TabsContent>
          
          <TabsContent value="list" className="space-y-4">
            <div className="grid gap-4">
              <div>
                <h3 className="font-semibold mb-3">キャラクター一覧</h3>
                <div className="grid gap-2">
                  {result.characters.map((char) => (
                    <div
                      key={char.id}
                      className={`card p-3 cursor-pointer transition-colors ${
                        selectedCharacter?.id === char.id ? 'border-primary' : ''
                      }`}
                      onClick={() => setSelectedCharacter(char)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{char.name}</span>
                            {char.isMain && (
                              <Badge variant="secondary">メイン</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{char.profile}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedCharacter && (
                <div>
                  <h3 className="font-semibold mb-3">
                    {selectedCharacter.name}の関係性
                  </h3>
                  <div className="grid gap-2">
                    {getCharacterRelationships(selectedCharacter.id).map((rel, idx) => {
                      const isSource = rel.source === selectedCharacter.id
                      const otherCharId = isSource ? rel.target : rel.source
                      const otherChar = result.characters.find(c => c.id === otherCharId)
                      const relationText = isSource ? rel.fromSourceToTarget : rel.fromTargetToSource
                      const reverseText = isSource ? rel.fromTargetToSource : rel.fromSourceToTarget
                      
                      return (
                        <div key={idx} className="card p-3">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge
                                style={{
                                  backgroundColor: RELATIONSHIP_TYPE_COLORS[rel.relationshipType as keyof typeof RELATIONSHIP_TYPE_COLORS] || '#9ca3af',
                                  color: 'white'
                                }}
                              >
                                {RELATIONSHIP_TYPE_LABELS[rel.relationshipType as keyof typeof RELATIONSHIP_TYPE_LABELS] || rel.relationshipType}
                              </Badge>
                              <span className="font-medium">{otherChar?.name}</span>
                              <Badge variant="outline">強度: {rel.strength}/10</Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <p>
                                <span className="text-muted-foreground">→</span> {relationText}
                              </p>
                              <p>
                                <span className="text-muted-foreground">←</span> {reverseText}
                              </p>
                              {rel.description && (
                                <p className="text-muted-foreground italic">{rel.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}